import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, Drawer, IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { SelectInput } from '@coverright/ui/inputs';
import { OfflineConversionSyncProcessFilterInput, EventName, Platform } from '@coverright/data-access/types/enrollment';
import { PeriodDateRange, PeriodDateRangeValue, getDateRangeFromPeriod } from '../../features';

interface DrawerProps {
  open: boolean;
  onClose: (filter?: OfflineConversionSyncProcessFilterInput) => void;
  initial?: OfflineConversionSyncProcessFilterInput;
}

interface ContextProps {
  open: (onCloseCallback: DrawerProps['onClose'], filters?: OfflineConversionSyncProcessFilterInput) => void;
}

const Context = React.createContext<ContextProps>({ open: () => {} });

export function withConversionSyncFilterDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [isOpen, setIsOpen] = useState(false);
    const [filters, setFilters] = useState<OfflineConversionSyncProcessFilterInput | undefined>();
    const callback = useRef<DrawerProps['onClose']>();

    const onClose: DrawerProps['onClose'] = filter => {
      setIsOpen(false);
      filter && callback.current && callback.current(filter);
    };

    const openDrawer = (onCloseCallback: DrawerProps['onClose'], filter?: OfflineConversionSyncProcessFilterInput) => {
      setFilters(filter);
      callback.current = onCloseCallback;
      setIsOpen(true);
    };

    return (
      <Context.Provider value={{ open: openDrawer }}>
        <ConversionSyncFilterDrawer open={isOpen} onClose={onClose} initial={filters} />
        <WrappedComponent />
      </Context.Provider>
    );
  };
}

export const useConversionSyncFilterDrawer = () => useContext(Context);

type LocalFilter = {
  createdAt?: PeriodDateRangeValue;
  eventName?: EventName | '';
  platform?: Platform | '';
  synced?: boolean | '';
  hasError?: boolean | '';
};

function ConversionSyncFilterDrawer({ open, onClose, initial }: DrawerProps) {
  const [filters, setFilters] = useState<LocalFilter>(toLocal(initial));

  useEffect(() => {
    setFilters(open ? toLocal(initial) : {});
  }, [open, initial]);

  const set = (key: keyof LocalFilter, value: any) =>
    setFilters(prev => ({ ...prev, [key]: value === '-' ? undefined : value ?? undefined }));

  const onSearch = () => onClose(toInput(filters));

  return (
    <Drawer anchor={'right'} open={open} onClose={() => onClose()}>
      <Box sx={{ p: 4, width: 420, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant={'h3'}>Conversion sync filters</Typography>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3, mt: 1 }} />

        <Stack component={'form'} onSubmit={(e: any) => e.preventDefault()} spacing={3}>
          <PeriodDateRange
            label={'Created at'}
            value={filters.createdAt || {}}
            onChange={value => set('createdAt', value)}
          />

          <SelectInput
            fullWidth
            label={'Event'}
            value={filters.eventName ?? ''}
            onChange={e => set('eventName', e.target.value)}
          >
            <MenuItem value={'-'}>Any</MenuItem>
            <MenuItem value={EventName.Lead}>Lead</MenuItem>
            <MenuItem value={EventName.Sql}>SQL</MenuItem>
            <MenuItem value={EventName.FollowUp}>Follow-up</MenuItem>
          </SelectInput>

          <SelectInput
            fullWidth
            label={'Platform'}
            value={filters.platform ?? ''}
            onChange={e => set('platform', e.target.value)}
          >
            <MenuItem value={'-'}>Any</MenuItem>
            <MenuItem value={Platform.GoogleCoverright}>Google CR</MenuItem>
            <MenuItem value={Platform.GoogleCaliburn}>Google Cal</MenuItem>
            <MenuItem value={Platform.MetaCoverright}>Meta CR</MenuItem>
            <MenuItem value={Platform.MicrosoftCoverright}>Microsoft CR</MenuItem>
            <MenuItem value={Platform.MicrosoftCaliburn}>Microsoft Cal</MenuItem>
          </SelectInput>

          <SelectInput
            fullWidth
            label={'Synced'}
            value={filters.synced === true ? 'true' : filters.synced === false ? 'false' : ''}
            onChange={e => set('synced', e.target.value === '' ? undefined : e.target.value === 'true')}
          >
            <MenuItem value={''}>Any</MenuItem>
            <MenuItem value={'true'}>Synced</MenuItem>
            <MenuItem value={'false'}>Pending</MenuItem>
          </SelectInput>

          <SelectInput
            fullWidth
            label={'Has error'}
            value={filters.hasError === true ? 'true' : filters.hasError === false ? 'false' : ''}
            onChange={e => set('hasError', e.target.value === '' ? undefined : e.target.value === 'true')}
          >
            <MenuItem value={''}>Any</MenuItem>
            <MenuItem value={'true'}>Yes</MenuItem>
            <MenuItem value={'false'}>No</MenuItem>
          </SelectInput>

          <div>
            <Button variant={'contained'} type={'submit'} onClick={onSearch}>Search</Button>
          </div>
        </Stack>
      </Box>
    </Drawer>
  );
}

function toLocal(input?: OfflineConversionSyncProcessFilterInput): LocalFilter {
  if (!input) return {};
  const createdAt: PeriodDateRangeValue | undefined =
    input.createdAtFrom || input.createdAtTo
      ? { range: { from: input.createdAtFrom, to: input.createdAtTo } }
      : undefined;
  return {
    createdAt,
    eventName: input.eventName ?? '',
    platform: input.platform ?? '',
    synced: input.synced ?? '',
    hasError: input.hasError ?? '',
  };
}

function toInput(local: LocalFilter): OfflineConversionSyncProcessFilterInput {
  const range = local.createdAt ? getDateRangeFromPeriod(local.createdAt) : undefined;
  return {
    ...(range?.from || range?.to ? { createdAtFrom: range.from, createdAtTo: range.to } : {}),
    ...(local.eventName ? { eventName: local.eventName as EventName } : {}),
    ...(local.platform ? { platform: local.platform as Platform } : {}),
    ...(local.synced !== '' && local.synced !== undefined ? { synced: local.synced as boolean } : {}),
    ...(local.hasError !== '' && local.hasError !== undefined ? { hasError: local.hasError as boolean } : {}),
  };
}
